import { useState, useEffect } from "react";
import {
  Stack,
  Typography,
  Alert,
  Button,
  CircularProgress,
} from "@mui/material";
import { Warning } from "@mui/icons-material";
import PrimaryButton from "./buttons/PrimaryButton";
import SecondaryButton from "./buttons/SecondaryButton";
import FollowUpTemplateDropdown from "./FollowUpTemplateDropdown";
import HostConflictResolution from "./HostConflictResolution";
import FollowUpModal from "./FollowUpModal";
import { CreateTemplateModal } from "./dialogs";
import { SkinnyMeetingDefinition } from "src/queries";
import { Guest } from "src/types";
import {
  useAssignFollowUpTemplate,
  HostConflict,
} from "src/mutations/useAssignFollowUpTemplate";
import { useFetchAllEventGuests } from "src/queries/useFetchAllEventGuests";
import useGeneralNotifications from "src/hooks/useGeneralNotifications";

interface FollowUpButtonProps {
  eventId: number;
  /** IDs of selected guests to assign templates to */
  selectedGuestIds: number[];
  onTemplateAssigned: () => void;
  onBeginFollowUp: (selectedGuestIds?: number[]) => void;
  onScheduleFollowUp: (delayHours: number, selectedGuestIds?: number[]) => void;
  eventStartTime: Date;
  eventEndTime: Date | null;
  eventDuration: number; // in minutes
  disabled?: boolean;
  /** Total count of guests needing host assignment (across all pages) */
  needsHostTotal?: number;
  /** Total count of guests ready for follow-up (across all pages) */
  readyForFollowUpTotal?: number;
}

export function FollowUpButton({
  eventId,
  selectedGuestIds,
  onTemplateAssigned,
  onBeginFollowUp,
  onScheduleFollowUp,
  eventStartTime,
  eventEndTime,
  eventDuration,
  disabled = false,
  needsHostTotal = 0,
  readyForFollowUpTotal = 0,
}: FollowUpButtonProps) {
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [selectedTemplate, setSelectedTemplate] =
    useState<SkinnyMeetingDefinition | null>(null);
  const [hostConflictError, setHostConflictError] = useState<string | null>(
    null,
  );
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [hostConflicts, setHostConflicts] = useState<HostConflict[]>([]);
  const [showConflictResolution, setShowConflictResolution] = useState(false);
  const [showCreateTemplateModal, setShowCreateTemplateModal] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [conflictsJustResolved, setConflictsJustResolved] = useState(false);
  const [readyGuests, setReadyGuests] = useState<Guest[]>([]);
  const [loadingReadyGuests, setLoadingReadyGuests] = useState(false);
  const [loadingConflicts, setLoadingConflicts] = useState(false);

  const assignFollowUpTemplate = useAssignFollowUpTemplate();
  const fetchAllEventGuests = useFetchAllEventGuests();
  const { addError } = useGeneralNotifications();

  const actualEndTime =
    eventEndTime ?? new Date(eventStartTime.getTime() + eventDuration * 60000);

  const hasEventElapsed = actualEndTime.getTime() < Date.now();

  // Show "Begin Follow Up" mode when:
  // 1. There are guests ready or needing hosts (templates have been assigned), AND
  // 2. No guests are currently selected (user is not trying to assign templates)
  // When guests are selected, show "Set up Follow Up" so user can assign templates
  const hasAssignedTemplates =
    (readyForFollowUpTotal > 0 || needsHostTotal > 0) &&
    selectedGuestIds.length === 0;

  // Show host conflict warning when there are guests needing hosts and not in selection mode
  useEffect(() => {
    if (
      needsHostTotal > 0 &&
      selectedGuestIds.length === 0 &&
      !showTemplateDropdown &&
      !conflictsJustResolved
    ) {
      const message =
        needsHostTotal === 1
          ? "One guest has a template assigned but no host."
          : `${needsHostTotal} guests have templates assigned but no hosts.`;
      setHostConflictError(message);
    }
  }, [
    needsHostTotal,
    selectedGuestIds.length,
    showTemplateDropdown,
    conflictsJustResolved,
  ]);

  // Reset the flag when fresh data arrives (no more guests needing hosts)
  useEffect(() => {
    if (conflictsJustResolved && needsHostTotal === 0) {
      setConflictsJustResolved(false);
    }
  }, [conflictsJustResolved, needsHostTotal]);

  // Clear host conflict error when:
  // 1. No more conflicts exist, OR
  // 2. User selects guests (switching to "Set up Follow Up" mode)
  useEffect(() => {
    if (
      hostConflictError &&
      (needsHostTotal === 0 || selectedGuestIds.length > 0)
    ) {
      setHostConflictError(null);
      setHostConflicts([]);
    }
  }, [hostConflictError, needsHostTotal, selectedGuestIds.length]);

  const handleSetUpFollowUp = () => {
    if (selectedGuestIds.length === 0) {
      return;
    }
    setShowTemplateDropdown(true);
  };

  const handleTemplateSelect = async (
    template: SkinnyMeetingDefinition | null,
  ) => {
    setSelectedTemplate(template);
    setHostConflictError(null);

    if (template) {
      try {
        // Attempt to assign the template - backend will handle guests that already have templates
        const assignmentResult = await assignFollowUpTemplate({
          eventId,
          contactIds: selectedGuestIds,
          templateId: template.id,
        });

        if (
          assignmentResult.conflicts &&
          assignmentResult.conflicts.length > 0
        ) {
          const conflictCount = assignmentResult.conflicts.length;
          const message =
            conflictCount === 1
              ? "One of the assigned contacts could not be matched with a host."
              : `${conflictCount} of the assigned contacts could not be matched with a host.`;
          setHostConflictError(message);
          setHostConflicts(assignmentResult.conflicts);
        }

        // Notify parent that assignment completed (with or without conflicts)
        onTemplateAssigned();
        setShowTemplateDropdown(false);
        setSelectedTemplate(null);
      } catch (error) {
        console.error("Error assigning follow-up template:", error);
        setHostConflictError(
          "Unable to assign follow-up template. Please try again.",
        );
      }
    }
  };

  const handleBackToGuestSelection = () => {
    setShowTemplateDropdown(false);
    setSelectedTemplate(null);
    setHostConflictError(null);
  };

  const handleFixHostConflicts = async () => {
    setLoadingConflicts(true);

    try {
      // Fetch ALL guests that need hosts (across all pages)
      const response = await fetchAllEventGuests(eventId, {
        followUpStatusFilter: "needs_host",
      });
      const allGuestsNeedingHosts = (response.guests as Guest[]) || [];

      // Convert to HostConflict format
      const allConflicts: HostConflict[] = allGuestsNeedingHosts.map(
        (guest, index) => ({
          contactId: guest.id,
          contactEmail: guest.email,
          contactName: `${guest.firstName} ${guest.lastName}`.trim(),
          reason: "No host assigned to follow-up template",
          key: `conflict-${guest.id}-${index}`,
        }),
      );

      setHostConflicts(allConflicts);
      setShowConflictResolution(true);
    } catch (error) {
      console.error("Error fetching guests needing hosts:", error);
      addError("Failed to load guests needing hosts. Please try again.");
    } finally {
      setLoadingConflicts(false);
    }
  };

  const handleCreateNewTemplate = () => {
    setShowCreateTemplateModal(true);
  };

  const handleTemplateCreated = (newTemplate: SkinnyMeetingDefinition) => {
    setSelectedTemplate(newTemplate);
    setShowCreateTemplateModal(false);
    setRefreshTrigger((prev) => prev + 1); // Trigger dropdown refresh
    // Small delay to ensure the template is fully saved before proceeding
    setTimeout(() => {
      handleTemplateSelect(newTemplate);
    }, 100);
  };

  const handleConflictResolved = () => {
    setHostConflictError(null);
    setHostConflicts([]);
    setConflictsJustResolved(true);
  };

  const handleBeginFollowUpClick = async () => {
    setLoadingReadyGuests(true);

    try {
      const response = await fetchAllEventGuests(eventId, {
        followUpStatusFilter: "ready",
      });
      setReadyGuests((response.guests as Guest[]) || []);
    } catch (error) {
      console.error("Error fetching ready guests:", error);
      addError("Failed to load ready guests. Please try again.");
      setReadyGuests([]);
    } finally {
      setShowFollowUpModal(true);
      setLoadingReadyGuests(false);
    }
  };

  const handleScheduleFollowUp = async (
    selectedGuestIds: number[],
    delayHours: number,
  ) => {
    onScheduleFollowUp(delayHours, selectedGuestIds);
  };

  const handleBeginFollowUpFromModal = async (selectedGuestIds: number[]) => {
    onBeginFollowUp(selectedGuestIds);
  };

  return (
    <>
      <Stack
        spacing={2}
        sx={{ width: "100%", maxWidth: "350px", marginLeft: "15px" }}
      >
        {showTemplateDropdown ? (
          <>
            <FollowUpTemplateDropdown
              value={selectedTemplate}
              onChange={handleTemplateSelect}
              disabled={disabled}
              onCreateNewTemplate={handleCreateNewTemplate}
              refreshTrigger={refreshTrigger}
            />

            <SecondaryButton onClick={handleBackToGuestSelection}>
              Back
            </SecondaryButton>

            {hostConflictError && (
              <Alert
                severity="warning"
                icon={<Warning />}
                action={
                  <Button
                    size="small"
                    onClick={handleFixHostConflicts}
                    disabled={loadingConflicts}
                    sx={{ textDecoration: "underline" }}
                  >
                    {loadingConflicts ? "Loading..." : "Fix This"}
                  </Button>
                }
              >
                <Typography variant="body2">{hostConflictError}</Typography>
              </Alert>
            )}
          </>
        ) : (
          <>
            {!hasAssignedTemplates ? (
              <PrimaryButton
                onClick={handleSetUpFollowUp}
                disabled={disabled || selectedGuestIds.length === 0}
                fullWidth
              >
                {selectedGuestIds.length > 0
                  ? `Set up Follow Up (${selectedGuestIds.length})`
                  : "Set up Follow Up"}
              </PrimaryButton>
            ) : (
              <>
                <PrimaryButton
                  onClick={handleBeginFollowUpClick}
                  disabled={
                    disabled ||
                    loadingReadyGuests ||
                    readyForFollowUpTotal === 0
                  }
                  fullWidth
                >
                  {loadingReadyGuests ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : hasEventElapsed ? (
                    `Begin Assigned Follow Up (${readyForFollowUpTotal})`
                  ) : (
                    `Schedule Follow Up (${readyForFollowUpTotal})`
                  )}
                </PrimaryButton>

                {hostConflictError && (
                  <Alert
                    severity="warning"
                    icon={<Warning />}
                    action={
                      <Button
                        size="small"
                        onClick={handleFixHostConflicts}
                        disabled={loadingConflicts}
                        sx={{ textDecoration: "underline" }}
                      >
                        {loadingConflicts ? "Loading..." : "Fix This"}
                      </Button>
                    }
                  >
                    <Typography variant="body2">{hostConflictError}</Typography>
                  </Alert>
                )}
              </>
            )}
          </>
        )}
      </Stack>

      {/* Modals rendered outside the main container to avoid z-index issues */}
      <HostConflictResolution
        open={showConflictResolution}
        onClose={() => setShowConflictResolution(false)}
        eventId={eventId}
        conflicts={hostConflicts}
        onResolved={handleConflictResolved}
      />

      <CreateTemplateModal
        open={showCreateTemplateModal}
        onClose={() => setShowCreateTemplateModal(false)}
        onTemplateCreated={handleTemplateCreated}
      />

      <FollowUpModal
        open={showFollowUpModal}
        onClose={() => setShowFollowUpModal(false)}
        selectedGuests={readyGuests}
        eventId={eventId}
        eventEndTime={actualEndTime}
        onLaunch={handleBeginFollowUpFromModal}
        onSchedule={handleScheduleFollowUp}
      />
    </>
  );
}
